// src/pages/PrivacyPolicyPage.js
import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-2xl font-bold text-gray-800 mb-3 border-l-4 border-blue-600 pl-4">{title}</h2>
    <div className="text-gray-600 leading-relaxed space-y-3 pl-4">{children}</div>
  </div>
);

function PrivacyPolicyPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-sky text-ink py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            className="text-3xl sm:text-5xl font-extrabold mb-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            className="text-slate-500 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Last updated: April 13, 2026
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <p className="text-gray-600 mb-10 leading-relaxed">
            At <strong>Kalyani Consultancy</strong>, we are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your data when you visit our website or engage our financial services. Please read it carefully.
          </p>

          <Section title="1. Information We Collect">
            <p>We may collect the following types of information:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Personal Identification Information:</strong> Name, email address, phone number, and postal address provided when you contact us or fill out a form.</li>
              <li><strong>Financial Information:</strong> Details you voluntarily share to help us provide insurance or investment advisory services (e.g., income range, existing policies).</li>
              <li><strong>Usage Data:</strong> Browser type, IP address, pages visited, and time spent on our website, collected automatically via cookies and analytics tools.</li>
            </ul>
          </Section>

          <Section title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Respond to your enquiries and provide personalised financial advice.</li>
              <li>Process premium payments and service requests.</li>
              <li>Send periodic emails regarding our services, updates, or promotions (you may opt out at any time).</li>
              <li>Improve our website and tailor the user experience.</li>
              <li>Comply with applicable laws and regulatory requirements (IRDAI, SEBI, AMFI).</li>
            </ul>
          </Section>

          <Section title="3. Sharing of Information">
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share data with:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Insurance companies or mutual fund houses as required to process your policy or investment.</li>
              <li>Trusted service providers who assist us in website operations, under strict confidentiality agreements.</li>
              <li>Regulatory or law enforcement authorities when required by law.</li>
            </ul>
          </Section>

          <Section title="4. Cookies">
            <p>
              Our website uses cookies to enhance your browsing experience. Cookies are small files placed on your device that help us understand how visitors use our site. You can choose to disable cookies through your browser settings; however, some parts of the website may not function correctly as a result.
            </p>
          </Section>

          <Section title="5. Data Security">
            <p>
              We implement appropriate technical and organisational security measures to protect your personal information from unauthorised access, alteration, disclosure, or destruction. While we strive to use commercially acceptable means to protect your data, no method of transmission over the internet is 100% secure.
            </p>
          </Section>

          <Section title="6. Retention of Data">
            <p>
              We retain your personal information only for as long as necessary to fulfil the purposes outlined in this policy, or as required by law. Financial records may be retained for a minimum of 8 years in compliance with regulatory guidelines.
            </p>
          </Section>

          <Section title="7. Your Rights">
            <p>You have the right to:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate or incomplete data.</li>
              <li>Request deletion of your data, subject to legal obligations.</li>
              <li>Withdraw consent for marketing communications at any time.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, please contact us at <a href="mailto:kalyaniconsultancy18@gmail.com" className="text-blue-600 hover:underline">kalyaniconsultancy18@gmail.com</a>.</p>
          </Section>

          <Section title="8. Third-Party Links">
            <p>
              Our website may contain links to third-party websites. We have no control over the content or privacy practices of those sites and accept no responsibility for them. We encourage you to review the privacy policies of any external sites you visit.
            </p>
          </Section>

          <Section title="9. Children's Privacy">
            <p>
              Our services are not directed at individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected such information, please contact us immediately.
            </p>
          </Section>

          <Section title="10. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </Section>

          <Section title="11. Contact Us">
            <p>If you have any questions or concerns about this Privacy Policy, please reach out to us:</p>
            <ul className="mt-3 space-y-1">
              <li><strong>Kalyani Consultancy</strong></li>
              <li>5th Floor, Akash Ganga Complex, Basudevpur, Haldia, West Bengal 721602</li>
              <li>📞 <a href="tel:+919476168969" className="text-blue-600 hover:underline">+91 94761 68969</a></li>
              <li>✉️ <a href="mailto:kalyaniconsultancy18@gmail.com" className="text-blue-600 hover:underline">kalyaniconsultancy18@gmail.com</a></li>
            </ul>
          </Section>
        </motion.div>
      </section>
    </>
  );
}

export default PrivacyPolicyPage;
